import React, { ChangeEvent, ReactNode, useState } from "react";
import {
    Body2,
    formGap,
    muiInputFormsCSS,
    sectionalMargin,
    StyledFieldLabel,
    StyledIconButton,
    Subtitle,
    inputLeftPadding,
    EmphasisText,
    FillSpace,
    styled,
    Header3,
} from "@iventis/styles";
import { Theme } from "@emotion/react";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { Divider, LinearProgress, TextField, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColorSelectorComponent } from "@iventis/components/src/selectors/colour-selector";
import { ChipType, StyledChip } from "@iventis/components/src/chips";
import { EMPTY_GUID } from "@iventis/utilities";
import { SitemapVersion } from "@iventis/domain-model/model/sitemapVersion";
import { CustomDialog, DisabledOverlay, FormButtonsComponent, SimpleTooltip, ToolTipWrapper } from "@iventis/components";
import { ConfirmationDialogComponent } from "@iventis/components/src/confirmation-dialog";
import { SitemapVersionLevel } from "@iventis/domain-model/model/sitemapVersionLevel";
import { MapObjectSitemapLevelReassignmentPayload } from "@iventis/domain-model/model/mapObjectSitemapLevelReassignmentPayload";
import { useSitemap } from "./hooks/use-sitemap";
import { useSitemapRoutingContext } from "./context/sitemap-routing-context";
import { useSitemapApiContext } from "./context/sitemap-api-context";
import { LevelReassignmentForm } from "./sitemap-version-level-reassignment-form";

export type SitemapVersionCopyProps = {
    /** Source sitemap version Id to copy from */
    sourceSitemapVersionId: string;
    sourceSitemapId: string;
    close: () => void;
};

export type CopyComponentRenderer = (internal: SitemapVersionCopyProps) => ReactNode;

export interface SitemapFormProps {
    copyComponent?: CopyComponentRenderer;
    projectName?: string;
}

export const SitemapForm = ({ copyComponent, projectName }: SitemapFormProps) => {
    const translate = useIventisTranslate();
    const theme = useTheme<Theme>();

    const { reassignSitemapLevels } = useSitemapApiContext();

    const { setSelectedSitemapId, setSelectedSitemapVersionId } = useSitemapRoutingContext();

    const { sitemap, setSitemap, isSitemapLoading, saveSitemap, isSaving, canSitemapSave, deleteSitemapVersion } = useSitemap();

    const [sitemapVersionLevelsForReassignment, setSitemapVersionLevelsForReassignment] = useState<SitemapVersionLevel[]>([]);
    const [sitemapVersionToCopy, setSitemapVersionToCopy] = useState<SitemapVersion | null>(null);
    const [sitemapVersionPendingDelete, setSitemapVersionPendingDelete] = useState<SitemapVersion | null>(null);

    const handleDeleteSitemapVersion = async (sitemapVersion: SitemapVersion) => {
        const allOtherSitemapVersionsLevelsAndIds = sitemap.versions.flatMap(({ id, sitemapVersionLevels }) =>
            sitemapVersion.id === id ? [] : sitemapVersionLevels.map((svl) => ({ id: svl.id, level: svl.levelIndex }))
        );

        const deletedLevelsWithNoMatchingOtherLevel = sitemapVersion.sitemapVersionLevels.filter(
            ({ levelIndex }) => !allOtherSitemapVersionsLevelsAndIds.some((svl) => svl.level === levelIndex)
        );

        if (deletedLevelsWithNoMatchingOtherLevel.length > 0) {
            setSitemapVersionLevelsForReassignment(deletedLevelsWithNoMatchingOtherLevel);
        } else {
            await deleteSitemapVersion(sitemapVersion.id);
        }
    };

    const handleLevelReassignment = async (reassignments: Omit<MapObjectSitemapLevelReassignmentPayload, "projectId">[]) => {
        const { sitemapVersionId } = sitemapVersionLevelsForReassignment[0];
        if (reassignments?.length > 0) {
            await reassignSitemapLevels(reassignments);
        }
        await deleteSitemapVersion(sitemapVersionId);
        setSitemapVersionLevelsForReassignment([]);
    };

    const getAvailableSitemapVersions = () => {
        const { sitemapVersionId } = sitemapVersionLevelsForReassignment[0];

        if (sitemapVersionId == null) {
            throw new Error("Can't find the sitemap version id that is being deleted");
        }

        return sitemap.versions.filter((version) => version.id !== sitemapVersionId);
    };

    const handleMapOrderChange = (event: ChangeEvent<HTMLInputElement>) => {
        const order = event.target.value;
        let parsedOrder = Number.parseInt(order, 10);
        if (parsedOrder < 1 || Number.isNaN(parsedOrder)) {
            parsedOrder = null;
        }
        setSitemap((s) => ({ ...s, mapOrder: parsedOrder }));
    };

    if (isSitemapLoading || sitemap == null) {
        return <LinearProgress />;
    }

    const hasMultipleVersions = sitemap?.versions?.length > 1;
    return (
        <>
            <Container>
                <Header3 className="header">
                    {sitemap.name == null || sitemap.name === "" ? <span>{translate(Content.sitemaps.sitemap.sitemap)}</span> : <span>{sitemap.name}</span>}
                </Header3>
                <StyledForm>
                    {/* NAME INPUT */}
                    <div className="sitemap-name sitemap-input-with-label">
                        <StyledFieldLabel>{translate(Content.sitemaps.sitemap.name)}</StyledFieldLabel>
                        <TextField
                            inputProps={{ "data-testid": "sitemap-name-input" }}
                            variant="outlined"
                            value={sitemap.name}
                            onChange={(event) => setSitemap((s) => ({ ...s, name: event.target.value }))}
                        />
                    </div>
                    {/* KEY INPUT */}
                    <div className="sitemap-key sitemap-input-with-label">
                        <StyledFieldLabel>{translate(Content.sitemaps.sitemap.key)}</StyledFieldLabel>
                        <TextField
                            inputProps={{ "data-testid": "sitemap-key-input" }}
                            variant="outlined"
                            value={sitemap.tag}
                            onChange={({ target: { value } }) =>
                                setSitemap((s) => ({ ...s, tag: value?.length > 3 ? value.toLocaleUpperCase().slice(0, 3) : value.toLocaleUpperCase() }))
                            }
                        />
                    </div>
                    {/* COLOUR INPUT */}
                    <div className="sitemap-colour sitemap-input-with-label">
                        <StyledFieldLabel>{translate(Content.sitemaps.sitemap.colour_of_label)}</StyledFieldLabel>
                        <ColorSelectorComponent
                            testId="sitemap-colour-input"
                            value={sitemap.colour ?? theme.tertiaryColors.primary}
                            changeValue={(value) => setSitemap((s) => ({ ...s, colour: value }))}
                        />
                    </div>
                    {/* MAP ORDER INPUT */}
                    <div className="sitemap-input-with-label">
                        <StyledFieldLabel>{translate(Content.sitemaps.sitemap.map_order)}</StyledFieldLabel>
                        <TextField type="number" value={sitemap.mapOrder ?? 0} onChange={handleMapOrderChange} />
                    </div>
                </StyledForm>
                {/* VERSIONS */}
                <StyledVersions>
                    <div className="versions-heading">
                        <Subtitle>{translate(Content.sitemaps.sitemap.versions)}</Subtitle>
                        <StyledChip
                            disabled={sitemap.id === EMPTY_GUID}
                            dataTestId="add-sitemap-version-button"
                            chipContainerClassName="add-version"
                            label={
                                <>
                                    <FontAwesomeIcon icon={["far", "plus"]} /> {translate(Content.sitemaps.sitemap.add_version)}
                                </>
                            }
                            chipType={ChipType.PrimaryButton}
                            onClick={() => setSelectedSitemapVersionId(EMPTY_GUID)}
                        />
                    </div>
                    {!(sitemap.versions?.length > 0) && <EmphasisText>{translate(Content.sitemaps.sitemap.there_are_no_versions)}</EmphasisText>}
                    <div className="versions-list" data-testid="sitemap-versions-list">
                        {sitemap.versions?.map((version) => (
                            <React.Fragment key={version.id}>
                                <Divider />
                                <StyledSitemapVersionRow>
                                    <Body2>{version.name}</Body2>
                                    <FillSpace />
                                    <SimpleTooltip text={!hasMultipleVersions && translate(Content.sitemaps.sitemapVersion.deleteVersionWarning)} placement="top">
                                        <ToolTipWrapper>
                                            {!hasMultipleVersions && <DisabledOverlay testId="sitemap-version-delete-disabled" opacity={0.75} />}
                                            <StyledIconButton
                                                data-testid={`delete-sitemap-version-button-${version.name}`}
                                                className="delete-button"
                                                onClick={() => setSitemapVersionPendingDelete(version)}
                                            >
                                                <span className="hidden">
                                                    {translate(Content.common.buttons.remove)} - {version.name}
                                                </span>
                                                <FontAwesomeIcon icon={["far", "trash"]} size="xs" />
                                            </StyledIconButton>
                                        </ToolTipWrapper>
                                    </SimpleTooltip>
                                    {/* Copy button - TODO: Add translation key for tooltip */}
                                    {copyComponent && (
                                        <SimpleTooltip text={version.sitemapVersionLevels?.length === 0 && "Cannot copy version with no active levels"} placement="top">
                                            <ToolTipWrapper>
                                                {version.sitemapVersionLevels?.length === 0 && <DisabledOverlay testId="sitemap-version-copy-disabled" opacity={0.75} />}
                                                <StyledIconButton data-testid={`copy-sitemap-version-button-${version.name}`} onClick={() => setSitemapVersionToCopy(version)}>
                                                    <span className="hidden">Copy - {version.name}</span>
                                                    <FontAwesomeIcon icon={["far", "copy"]} size="xs" />
                                                </StyledIconButton>
                                            </ToolTipWrapper>
                                        </SimpleTooltip>
                                    )}
                                    <StyledIconButton data-testid={`edit-sitemap-version-button-${version.name}`} onClick={() => setSelectedSitemapVersionId(version.id)}>
                                        <span className="hidden">
                                            {translate(Content.sitemaps.sitemapVersion.title.edit)} - {version.name}
                                        </span>
                                        <FontAwesomeIcon icon={["far", "pen-to-square"]} size="xs" />
                                    </StyledIconButton>
                                </StyledSitemapVersionRow>
                            </React.Fragment>
                        ))}
                    </div>
                </StyledVersions>
                <FormButtonsContainer>
                    <FormButtonsComponent
                        handleSubmit={saveSitemap}
                        isSubmitting={isSaving}
                        disableSubmit={() => !canSitemapSave}
                        submitButtonText={translate(Content.common.buttons.save)}
                        secondaryButtons={[{ buttonText: translate(Content.common.buttons.back), onButtonPressed: () => setSelectedSitemapId(null) }]}
                    />
                </FormButtonsContainer>
            </Container>
            <CustomDialog open={sitemapVersionLevelsForReassignment.length > 0}>
                {sitemapVersionLevelsForReassignment.length > 0 && (
                    <LevelReassignmentForm
                        availableSitemapVersions={getAvailableSitemapVersions()}
                        existingLevelsRemoved={sitemapVersionLevelsForReassignment}
                        confirm={handleLevelReassignment}
                        close={() => setSitemapVersionLevelsForReassignment([])}
                    />
                )}
            </CustomDialog>
            {copyComponent && sitemapVersionToCopy && (
                <CustomDialog open={sitemapVersionToCopy != null}>
                    {copyComponent({ sourceSitemapVersionId: sitemapVersionToCopy.id, close: () => setSitemapVersionToCopy(null), sourceSitemapId: sitemap.id })}
                </CustomDialog>
            )}
            <ConfirmationDialogComponent
                show={sitemapVersionPendingDelete != null}
                handleConfirm={async () => {
                    if (sitemapVersionPendingDelete != null) {
                        await handleDeleteSitemapVersion(sitemapVersionPendingDelete);
                    }
                    setSitemapVersionPendingDelete(null);
                }}
                handleCancel={() => setSitemapVersionPendingDelete(null)}
                title="Delete sitemap version"
                confirmText={translate(Content.common.buttons.confirm)}
                cancelText={translate(Content.common.buttons.cancel)}
                message={`${translate(Content.common.delete_confirmation_message, { entity: sitemapVersionPendingDelete?.name })} | Project: ${projectName ?? ""} | Sitemap: ${
                    sitemap?.name ?? ""
                }.`}
                dataTestId="confirm-delete-sitemap-version"
            />
        </>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-items: space-between;
    height: 100%;

    .header {
        margin-bottom: 10px;
    }
`;

export const StyledSitemapVersionRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: ${inputLeftPadding};
`;

const StyledVersions = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: ${formGap};
    grid-area: versions;
    min-height: 0; /* Ensure it can shrink properly */
    flex: 1;
    .versions-heading {
        display: flex;
        justify-content: space-between;
        align-items: end;
    }
    .versions-list {
        overflow-y: auto;
        flex: 1;
        min-height: 0; /* Ensure it can shrink properly */
    }
`;

const StyledForm = styled.div`
    ${muiInputFormsCSS}
    display: grid;
    grid-template-areas:
        "name key"
        "colour ."
        "versions versions";
    gap: ${sectionalMargin};
    .sitemap-name {
        grid-area: name;
    }
    .sitemap-key {
        grid-area: key;
    }
    .sitemap-colour {
        grid-area: colour;
    }
    .sitemap-input-with-label {
        display: flex;
        flex-direction: column;
    }
`;

const FormButtonsContainer = styled.div`
    display: flex;
`;
